import { MedGEOLogoComponent } from 'assets/MedGEOlogo'
import { procedureStatusBarHeight } from 'components/ProcedureStatusBar'
import { useState, useEffect } from 'react'
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Toolbar,
  Typography,
  Tooltip,
  ListItem,
} from '@mui/material'
import {
  Edit,
  ExpandLess,
  Logout,
  Menu,
  Notifications,
  Settings,
} from '@mui/icons-material'
import styles from './floatingBar.module.css'
import {
  ANALYTICS,
  REFERRING_PHYSICIANS,
  SCRUB_TECHNICIANS,
  CATALOG,
  PATIENT_REPORT,
  ROOM_VIEW,
  PRODUCT_REPORTS,
  PRODUCT_EMAIL_SERVICE,
} from 'app/ROUTES_CONSTANTS'
import { useFloatingBarLogic } from './FloatingBar.logic'
import { useUser } from 'app/User'
import NotificationPopup from 'components/NotificationPopup'
import SurgeryReminderModal from 'components/SurgeryReminderModal/SurgeryReminderModal'
import RoomViewSettingsPopup from 'views/RoomView/RoomViewSettingsPopup'
import DepartmentSelector from 'components/DepartmentSelector'
import { useEnterpriseStore } from 'store'
import { useIsMobile } from 'assets/utils/mediaQueries'

const isStaging = process.env.REACT_APP_ENV === 'staging'

function FloatingBar() {
  const logic = useFloatingBarLogic()
  const { isGuest, isRep } = useUser()
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  const isMobile = useIsMobile()

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const { departments } = useEnterpriseStore()

  const renderLogo = () => {
    return (
      <Button
        onClick={
          logic.isEditingSubmittedSurgery ? undefined : logic.handleLogoBtnClick
        }
        sx={{
          m: 0,
          p: 0,
          width: '120px',
          cursor:
            (isGuest && !isRep) || logic.isEditingSubmittedSurgery
              ? 'default'
              : 'pointer',
        }}
      >
        <Box display="flex">
          <MedGEOLogoComponent color="white" scale={0.6} />
        </Box>
      </Button>
    )
  }

  return (
    <AppBar
      id="medgeo-app-header"
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: logic.isEditingSubmittedSurgery
          ? 'grayscale.main'
          : isStaging
          ? 'darkgreen'
          : 'primary.main',
      }}
    >
      <Toolbar>
        <Box className={styles.toolbar}>
          <Box className={styles.heading} sx={{ width: 1, color: 'white' }}>
            {renderLogo()}
            <>
              <Typography sx={{ mx: 1, fontSize: '1.5rem', fontWeight: 300 }}>
                |
              </Typography>
              {isMobile ? (
                departments.length === 0 ? (
                  <Typography>{logic.dynamicTitle}</Typography>
                ) : null
              ) : (
                <Typography>{logic.dynamicTitle}</Typography>
              )}
            </>
          </Box>
          {logic.isEditingSubmittedSurgery && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
              }}
            >
              <Typography variant="h3">Addendum Mode</Typography>
            </Box>
          )}
          {logic.user && !logic.isEditingSubmittedSurgery && (
            <Box flexDirection={'row'} display="flex" alignItems="center">
              {!logic.shouldHideDepartmentSwitcher && (
                <DepartmentSelector variant="navbar" />
              )}

              <Box sx={{ position: 'relative' }}>
                {!isRep && (
                  <Tooltip title="Notifications" placement="left">
                    <IconButton
                      sx={{ color: 'primary.lightest' }}
                      onClick={logic.handleNotificationToggle}
                    >
                      <Badge
                        badgeContent={logic.notifications.length}
                        max={99}
                        sx={{
                          '& .MuiBadge-badge': {
                            color: 'white',
                            backgroundColor: 'error.main',
                          },
                        }}
                      >
                        <Notifications />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
                <NotificationPopup
                  isOpen={logic.isNotificationPopupOpen}
                  onClose={() => logic.handleNotificationToggle()}
                  notifications={logic.notifications}
                  isLoading={logic.isLoadingProcedures}
                  filterPeriod={logic.filterPeriod}
                  onFilterChange={logic.handleFilterChange}
                />
              </Box>
              {logic.showSettings[ROOM_VIEW] && (
                <Box className={styles.settingsContainer}>
                  <Tooltip title="Settings" placement="right">
                    <IconButton
                      data-testid="settings-button"
                      onClick={logic.handleSettingsToggle}
                      className={styles.settingsButton}
                    >
                      <Settings />
                    </IconButton>
                  </Tooltip>
                  <RoomViewSettingsPopup
                    isOpen={logic.isSettingsPopupOpen}
                    onClose={logic.handleSettingsToggle}
                  />
                </Box>
              )}
              <Tooltip title="User Menu" placement="left">
                <IconButton
                  data-testid="menu-button"
                  sx={{ color: 'primary.lightest' }}
                  onClick={logic.handleMenuToggle}
                >
                  <Menu />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {logic.isEditingSubmittedSurgery && (
            <Button
              onClick={logic.handleExitAddendumMode}
              sx={{
                color: 'white',
                border: '1px solid white',
                '&:hover': {
                  border: '1px solid white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Exit
            </Button>
          )}
        </Box>
      </Toolbar>

      {/* Menu */}
      {!logic.isEditingSubmittedSurgery && (
        <Drawer
          open={logic.isMenuOpen}
          onClose={logic.handleMenuToggle}
          variant="temporary"
          anchor="top"
          sx={{ zIndex: 1000 }}
        >
          <List>
            <ListItem
              sx={{
                bgcolor: logic.isEditingSubmittedSurgery
                  ? 'grayscale.main'
                  : isStaging
                  ? 'darkgreen'
                  : 'primary.main',
                color: 'white',
                justifyContent: 'space-between',
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'white', color: 'primary.main' }} />
              </ListItemAvatar>
              <ListItemText>
                {`${logic.userRoleString?.toUpperCase()}`}
                {(logic.user?.firstName || logic.user?.lastName) &&
                  ` | ${logic.user?.firstName ?? ''} ${
                    logic.user?.lastName ?? ''
                  }`}
              </ListItemText>
              <IconButton
                onClick={logic.handleMenuToggle}
                sx={{ color: 'white' }}
              >
                <ExpandLess />
              </IconButton>
            </ListItem>

            {/* USER & ACTIONS */}
            {logic.user?.id && !logic.isGuest && (
              <ListItemButton
                data-testid="profile-button"
                onClick={() => logic.handleNavigate('profile')}
                sx={{ pt: 2 }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: 'grayscale.light' }}>
                    <Edit
                      sx={{
                        color: 'primary.main',
                        backgroundColor: 'grayscale.light',
                      }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText>Profile</ListItemText>
              </ListItemButton>
            )}
            <ListItemButton
              data-testid="logout-button"
              divider={!logic.isGuest}
              onClick={logic.handleLogout}
            >
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: 'grayscale.light' }}>
                  <Logout
                    sx={{
                      color: 'primary.main',
                      backgroundColor: 'grayscale.light',
                    }}
                  />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="Logout" />
            </ListItemButton>

            {/* APP NAVIGATION */}
            {!logic.isSPD && !logic.isGuest && (
              <ListItemButton
                data-testid="surgery-list"
                onClick={() => logic.handleNavigate('surgeries')}
              >
                <ListItemText>Procedure List</ListItemText>
              </ListItemButton>
            )}
            {logic.flags.navigationMenuTrayManagement && !logic.isGuest && (
              <ListItemButton
                data-testid="surgery-list"
                onClick={() => logic.handleNavigate('tray-management')}
              >
                <ListItemText>Tray Management {'(DTM)'}</ListItemText>
              </ListItemButton>
            )}
            {logic.flags.navigationMenuSpd && logic.isSPD && (
              <ListItemButton
                data-testid="surgery-list"
                onClick={() => logic.handleNavigate('spd')}
              >
                <ListItemText>SPD {'(DTM)'}</ListItemText>
              </ListItemButton>
            )}
            {logic.isNurseAdmin && (
              <ListItemButton onClick={() => logic.handleNavigate(ANALYTICS)}>
                <ListItemText>Analytics</ListItemText>
              </ListItemButton>
            )}
            {logic.isNurseAdmin &&
              logic.flags.newImplantSurgeryReportSender && (
                <ListItemButton
                  onClick={() => logic.handleNavigate(PRODUCT_EMAIL_SERVICE)}
                >
                  <ListItemText>Product Email Service</ListItemText>
                </ListItemButton>
              )}
            {logic.isNurseAdmin && logic.flags.catalogPluginEnabled && (
              <ListItemButton onClick={() => logic.handleNavigate(CATALOG)}>
                <ListItemText>Catalog</ListItemText>
              </ListItemButton>
            )}
            {logic.flags.navigationMenuInventory && logic.isNurseAdmin && (
              <ListItemButton
                onClick={() => logic.handleNavigate('inventory-report')}
              >
                <ListItemText>Inventory</ListItemText>
              </ListItemButton>
            )}
            {logic.flags.navigationMenuReceiveProducts &&
              logic.isNurseAdmin && (
                <ListItemButton onClick={() => logic.handleNavigate('receive')}>
                  <ListItemText>Receive Products</ListItemText>
                </ListItemButton>
              )}
            {logic.isNurseAdmin &&
              logic.flags.navigationMenuPreferenceCards && (
                <ListItemButton
                  onClick={() => logic.handleNavigate('preference-cards')}
                >
                  <ListItemText>Preference Cards</ListItemText>
                </ListItemButton>
              )}
            {logic.isNurseAdmin && (
              <ListItemButton
                onClick={() => logic.handleNavigate(REFERRING_PHYSICIANS)}
              >
                <ListItemText>{logic.PhysiciansTitle}</ListItemText>
              </ListItemButton>
            )}
            {logic.isNurseAdmin && logic.showScrubTechnician && (
              <ListItemButton
                onClick={() => logic.handleNavigate(SCRUB_TECHNICIANS)}
              >
                <ListItemText>Scrub Technicians</ListItemText>
              </ListItemButton>
            )}
            {logic.isNurseAdmin && (
              <ListItemButton
                onClick={() => logic.handleNavigate(PATIENT_REPORT)}
              >
                <ListItemText>Patient Report</ListItemText>
              </ListItemButton>
            )}
            {logic.isNurseAdmin && (
              <ListItemButton
                onClick={() => logic.handleNavigate(PRODUCT_REPORTS)}
              >
                <ListItemText>Product Reports</ListItemText>
              </ListItemButton>
            )}
          </List>
        </Drawer>
      )}

      {/* Offline Indicator */}
      {logic.user && (
        <Collapse in={!isOnline} className={styles.offlineIndicator}>
          <Box
            sx={{
              backgroundColor: 'error.main',
              height: procedureStatusBarHeight + 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                my: 0,
                mx: 2,
                textAlign: 'center',
                textTransform: 'uppercase',
              }}
            >
              You are offline
            </Typography>
          </Box>
        </Collapse>
      )}

      <SurgeryReminderModal />
    </AppBar>
  )
}

export default FloatingBar
